import {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber as isValidPhoneNumberForCountry,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

export type { CountryCode };

export const DEFAULT_COUNTRY: CountryCode = 'US';

export interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
  flag: string;
}

/**
 * Converts an ISO 3166-1 alpha-2 country code into its flag emoji by mapping
 * each letter to its Unicode regional indicator symbol.
 */
export const getFlagEmoji = (countryCode: CountryCode): string =>
  countryCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

const getCountryName = (countryCode: CountryCode): string => countryNames.of(countryCode) ?? countryCode;

// Penny's FlagIcon has no flag asset for these three (as of @melio/penny 67.0.1),
// so they're excluded rather than shown with a broken flag.
const COUNTRIES_WITHOUT_PENNY_FLAGS = new Set(['AC', 'PS', 'TA']);

/**
 * Every country libphonenumber-js knows about, sorted alphabetically by
 * display name. Built at module load so adding a country requires no
 * hand-maintained list.
 */
export const SUPPORTED_COUNTRIES: CountryOption[] = getCountries()
  .filter((code) => !COUNTRIES_WITHOUT_PENNY_FLAGS.has(code))
  .map((code) => ({
    code,
    name: getCountryName(code),
    callingCode: getCountryCallingCode(code),
    flag: getFlagEmoji(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Filters countries by name or calling code, so searching "united" and searching "1" (or "+1")
 * both find the United States - matching either field a user is likely to actually know.
 */
export const filterCountries = (countries: CountryOption[], query: string): CountryOption[] => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return countries;
  }

  const digitsOnly = trimmed.replace(/\D/g, '');
  return countries.filter(
    (country) => country.name.toLowerCase().includes(trimmed) || (digitsOnly !== '' && country.callingCode.includes(digitsOnly))
  );
};

export const findCountry = (countryCode: CountryCode): CountryOption =>
  SUPPORTED_COUNTRIES.find((country) => country.code === countryCode) ?? {
    code: countryCode,
    name: getCountryName(countryCode),
    callingCode: getCountryCallingCode(countryCode),
    flag: getFlagEmoji(countryCode),
  };

/**
 * Formats digits as the user types, following the selected country's
 * national format (e.g. "2025551234" -> "(202) 555-1234" for US).
 */
export const formatAsYouType = (digits: string, country: CountryCode): string => new AsYouType(country).input(digits);

export const isValidPhoneNumber = (nationalNumber: string, country: CountryCode): boolean => {
  const stripped = nationalNumber.replace(/\D/g, '');
  const callingCode = getCountryCallingCode(country);
  // Reject a redundant leading country-calling-code digit (libphonenumber-js accepts it on its own).
  if (stripped.startsWith(callingCode) && isValidPhoneNumberForCountry(stripped.slice(callingCode.length), country)) {
    return false;
  }
  return isValidPhoneNumberForCountry(nationalNumber, country);
};

/**
 * Returns the E.164 representation (e.g. "+12025551234") or undefined if the
 * number can't be parsed for the given country.
 */
export const toE164 = (nationalNumber: string, country: CountryCode): string | undefined =>
  parsePhoneNumberFromString(nationalNumber, country)?.number;

/**
 * Formats an E.164 number for display in international format (e.g. "+12025551234" -> "+1 202 555 1234").
 * Falls back to the input unchanged if it can't be parsed.
 */
export const formatE164ForDisplay = (e164: string): string => parsePhoneNumberFromString(e164)?.formatInternational() ?? e164;
